'use client'

import { useState, useEffect, useRef } from 'react'
import Script from 'next/script'
import { useSubscription } from '@/lib/hooks/use-subscription'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Send, Upload, FileText, Settings, X, Plus, Trash2, 
  Sparkles, File as FileIcon, Paperclip, ChevronLeft, Loader2, Download, MessageSquare,
  PenLine, User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { Badge } from '@/components/ui/badge'
import { createBrowserClient } from '@supabase/ssr'
import ReactMarkdown from 'react-markdown'
import { useToast } from '@/hooks/use-toast'
import { hasAccess, SUBSCRIPTION_TIERS } from '@/config/subscriptions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
  conversation_id?: string // Optional: tracks which conversation this message belongs to
  metadata?: {
    template?: string
    section?: string
    tone?: string
    length?: string
    use_rag?: boolean
    attached_documents?: string[]
  }
}

interface Conversation {
  id: string
  title: string | null
  bid_type: string | null
  output_format: string | null
  created_at: string
  updated_at: string
}

interface OpportunityDocument {
  id: string
  file_name: string
  file_type: string | null
  size: number | null
  status: string
  opportunity_title: string | null
  opportunity_number: string | null
  opportunity_agency: string | null
  created_at: string
}

export default function AIProposalGeneratorPage() {
  const { tier, isLoading: isSubscriptionLoading } = useSubscription()
  const { toast } = useToast()
  const isProfessional = hasAccess(tier || 'free', SUBSCRIPTION_TIERS.professional)
  const isMobile = useIsMobile()
  const supabase = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  )

  // State
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadedDocuments, setUploadedDocuments] = useState<OpportunityDocument[]>([])
  const [attachedDocumentIds, setAttachedDocumentIds] = useState<string[]>([])
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string>('gemini-2.5')
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false)

  // Get current conversation title for mobile header
  const currentConversation = conversations.find(c => c.id === currentConversationId)

  // Truncate text to a maximum number of words
  const truncateWords = (text: string, maxWords: number = 4): string => {
    if (!text) return 'Untitled Proposal'
    const words = text.split(/\s+/)
    if (words.length <= maxWords) return text
    return words.slice(0, maxWords).join(' ') + '...'
  }

  // Settings
  const [bidType, setBidType] = useState<string>('RFP')
  const [outputFormat, setOutputFormat] = useState<string>('text')
  const [tone, setTone] = useState<string>('professional')
  const [length, setLength] = useState<string>('medium')
  const [useRAG, setUseRAG] = useState<boolean>(true)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isSendingMessageRef = useRef(false)

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations()
  }, [])

  // Fetch messages when conversation changes
  // Skip fetching if messages already exist (to prevent overwriting local messages)
  useEffect(() => {
    if (currentConversationId) {
      // Only fetch if we don't have messages or if messages are from a different conversation
      // This prevents clearing messages when switching to a conversation we're actively using
      const shouldFetch = messages.length === 0 || 
        !messages.some(msg => msg.conversation_id === currentConversationId)
      
      if (shouldFetch) {
        fetchMessages(currentConversationId)
      }
      fetchUploadedDocuments()
    } else {
      setMessages([])
      setUploadedDocuments([]) // Clear documents when no conversation
    }
  }, [currentConversationId])

  // Poll for document status updates (every 3 seconds while documents are processing)
  useEffect(() => {
    if (!currentConversationId) return

    const hasProcessingDocs = uploadedDocuments.some(
      doc => doc.status === 'uploaded' || doc.status === 'processing'
    )

    if (!hasProcessingDocs) return

    const interval = setInterval(() => {
      fetchUploadedDocuments()
    }, 3000)

    return () => clearInterval(interval)
  }, [currentConversationId, uploadedDocuments])

  const fetchConversations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('proposal_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (error) throw error
      setConversations(data || [])
        } catch (error) {
      console.error('Error fetching conversations:', error)
      toast({
        title: 'Error',
        description: 'Failed to load conversations',
        variant: 'destructive',
      })
    }
  }

  const fetchMessages = async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('proposal_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })

      if (error) throw error
      setMessages(data || [])
    } catch (error) {
      console.error('Error fetching messages:', error)
    }
  }

  const fetchUploadedDocuments = async (overrideConversationId?: string | null) => {
    try {
      const convId = overrideConversationId ?? currentConversationId
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      if (!convId) {
        setUploadedDocuments([])
        return
      }

      const { data, error } = await supabase
        .from('opportunity_document_uploads')
        .select('*')
        .eq('user_id', user.id)
        .eq('conversation_id', convId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching documents:', error)
        throw error
      }
      
      setUploadedDocuments(data || [])
    } catch (error) {
      console.error('Error fetching documents:', error)
      setUploadedDocuments([])
    }
  }

  const createNewConversation = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('proposal_conversations')
        .insert({
          user_id: user.id,
          title: 'New Proposal',
          bid_type: bidType,
          output_format: outputFormat,
        })
        .select()
        .single()

      if (error) throw error
      
      setCurrentConversationId(data.id)
      setMessages([])
      setUploadedDocuments([]) // Clear documents for new conversation
      await fetchConversations()
    } catch (error) {
      console.error('Error creating conversation:', error)
      toast({
        title: 'Error',
        description: 'Failed to create new conversation',
        variant: 'destructive',
      })
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(prev => [...prev, ...files])
  }

  // Extract text from PDF in the browser
  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      // Only run in browser
      if (typeof window === 'undefined') {
        throw new Error('PDF extraction only works in browser')
      }

      // Wait for PDF.js to be available (loaded via Script component)
      // Check multiple possible global names - unpkg UMD build uses pdfjsLib
      let pdfjsLib = (window as any).pdfjsLib || (window as any).pdfjsDist || (window as any).pdfjs
      
      if (!pdfjsLib) {
        // Wait for script to load (with timeout)
        await new Promise<void>((resolve, reject) => {
          const checkInterval = setInterval(() => {
            pdfjsLib = (window as any).pdfjsLib || (window as any).pdfjsDist || (window as any).pdfjs
            if (pdfjsLib) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
          
          setTimeout(() => {
            clearInterval(checkInterval)
            pdfjsLib = (window as any).pdfjsLib || (window as any).pdfjsDist || (window as any).pdfjs
            if (pdfjsLib) {
              resolve()
            } else {
              reject(new Error('PDF.js not loaded. Please refresh the page and try again.'))
            }
          }, 10000) // 10 second timeout
        })
      }

      // Ensure we have the library
      pdfjsLib = (window as any).pdfjsLib || (window as any).pdfjsDist || (window as any).pdfjs
      if (!pdfjsLib) {
        throw new Error('PDF.js library not found. Please refresh the page.')
      }
      
      // Set worker source if not already set
      if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'
      }
      
      // Read file as array buffer
      const arrayBuffer = await file.arrayBuffer()
      
      // Get getDocument function
      const getDocument = pdfjsLib.getDocument || pdfjsLib.default?.getDocument
      if (!getDocument) {
        throw new Error('getDocument function not found in PDF.js')
      }
      
      // Load PDF document
      const loadingTask = getDocument({ data: arrayBuffer })
      const pdfDocument = await loadingTask.promise
      
      // Extract text from all pages
      const textParts: string[] = []
      for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum)
        const textContent = await page.getTextContent()
        const pageText = textContent.items
          .map((item: any) => item.str || '')
          .join(' ')
        textParts.push(pageText)
      }
      
      return textParts.join('\n\n')
    } catch (error: any) {
      console.error('Error extracting text from PDF:', error)
      throw new Error(`Failed to extract text from PDF: ${error.message}`)
    }
  }

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Ensure we have a conversation
      let conversationId = currentConversationId
      if (!conversationId) {
        // Create a new conversation if none exists
        const { data: newConv, error: convError } = await supabase
          .from('proposal_conversations')
          .insert({
            user_id: user.id,
            title: 'New Proposal',
            bid_type: bidType,
            output_format: outputFormat,
          })
          .select()
          .single()

        if (convError || !newConv) {
          throw new Error('Failed to create conversation')
        }
        conversationId = newConv.id
        setCurrentConversationId(newConv.id)
        await fetchConversations()
      }

      setIsLoading(true)

      for (const file of selectedFiles) {
        let fileToUpload = file
        let fileName = file.name
        let fileType = file.type
        let fileSize = file.size

        // If it's a PDF, extract text and create a text file instead
        if (file.type === 'application/pdf') {
          try {
            toast({
              title: 'Extracting PDF text...',
              description: `Processing ${file.name}...`,
            })
            
            const extractedText = await extractTextFromPDF(file)
            
            if (!extractedText || extractedText.trim().length === 0) {
              throw new Error('No text content extracted from PDF')
            }
            
            // Create a text file from extracted text
            const textBlob = new Blob([extractedText], { type: 'text/plain' })
            const textFileName = file.name.replace(/\.pdf$/i, '.txt')
            
            // Create File object from Blob - use the browser's File constructor
            // We need to use the global File, not any imported one
            const FileConstructor = (window as any).File || File
            fileToUpload = new FileConstructor([textBlob], textFileName, { 
              type: 'text/plain',
              lastModified: Date.now()
            })
            fileName = textFileName
            fileType = 'text/plain'
            fileSize = textBlob.size
            
            toast({
              title: 'PDF text extracted',
              description: `Extracted ${extractedText.length} characters from PDF`,
            })
          } catch (pdfError: any) {
            console.error('Error extracting PDF text:', pdfError)
            toast({
              title: 'PDF Extraction Failed',
              description: pdfError.message || 'Could not extract text from PDF. Please convert to text format manually and try again.',
              variant: 'destructive',
            })
            // Skip this file - don't upload PDFs that can't be extracted
            continue
          }
        }

        const timestamp = Date.now()
        const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.]/g, '_')
        const filePath = `${user.id}/opportunity-docs/${timestamp}_${sanitizedFileName}`

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('company.documents')
          .upload(filePath, fileToUpload, {
            cacheControl: '3600',
            upsert: false,
            contentType: fileType,
          })

        if (uploadError) throw uploadError

        // Create database record with processing status
        const { data: docData, error: dbError } = await supabase
          .from('opportunity_document_uploads')
          .insert({
            user_id: user.id,
            conversation_id: conversationId, // Use the conversationId variable
            file_name: fileName,
            storage_object_path: filePath,
            file_type: fileType,
            size: fileSize,
            status: 'uploaded', // Will be updated to 'processing' then 'indexed'
          })
          .select()
          .single()

        // Update conversation's attached_document_ids
        if (conversationId && docData) {
          const { data: conv } = await supabase
            .from('proposal_conversations')
            .select('attached_document_ids')
            .eq('id', conversationId)
            .single()
          
          const currentDocs = conv?.attached_document_ids || []
          if (!currentDocs.includes(docData.id)) {
            await supabase
              .from('proposal_conversations')
              .update({ 
                attached_document_ids: [...currentDocs, docData.id],
                updated_at: new Date().toISOString()
              })
              .eq('id', conversationId)
          }
        }

        if (dbError) {
          await supabase.storage.from('company.documents').remove([filePath])
          throw dbError
        }

        if (docData) {
          setUploadedDocuments(prev => {
            const exists = prev.some(d => d.id === docData.id)
            return exists ? prev : [docData, ...prev]
          })
        }

        // Process document asynchronously (manual-index-processor will update status to 'processing')
        processDocument(docData.id, filePath, file.type, file.name).catch(async (err) => {
          console.error(`Error processing document ${docData.id}:`, err)
          await supabase
            .from('opportunity_document_uploads')
            .update({ 
              status: 'error',
              updated_at: new Date().toISOString()
            })
            .eq('id', docData.id)
          await fetchUploadedDocuments(conversationId)
        })
      }

      setSelectedFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      await fetchUploadedDocuments(conversationId)
      
      toast({
        title: 'Success',
        description: `${selectedFiles.length} document(s) uploaded. Processing and analyzing...`,
      })
    } catch (error: any) {
      console.error('Error uploading files:', error)
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload documents',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const removeDocument = async (docId: string) => {
    try {
      if (!currentConversationId) return

      // Update document to remove conversation_id (but keep the document for user)
      const { error } = await supabase
        .from('opportunity_document_uploads')
        .update({ conversation_id: null })
        .eq('id', docId)

      if (error) throw error

      // Update conversation's attached_document_ids
      const { data: conv } = await supabase
        .from('proposal_conversations')
        .select('attached_document_ids')
        .eq('id', currentConversationId)
        .single()

      if (conv?.attached_document_ids) {
        const updatedDocs = conv.attached_document_ids.filter((id: string) => id !== docId)
        await supabase
          .from('proposal_conversations')
          .update({ 
            attached_document_ids: updatedDocs,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentConversationId)
      }

      // Refresh document list
      await fetchUploadedDocuments()

      toast({
        title: 'Document Removed',
        description: 'Document has been removed from this conversation.',
      })
    } catch (error: any) {
      console.error('Error removing document:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove document',
        variant: 'destructive',
      })
    }
  }

  const analyzeDocument = async (docId: string, convId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/analyze-opportunity-document`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
            ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            document_id: docId,
            conversation_id: convId,
            user_id: user.id,
          }),
        }
      )

      if (!response.ok) {
        console.warn('Document analysis failed, but document is indexed')
        return
      }

      const data = await response.json()
      console.log(`Document analyzed: ${data.todos_created || 0} todos created`)
    } catch (error) {
      console.error('Error analyzing document:', error)
      // Don't throw - analysis is optional
    }
  }

  const processDocument = async (docId: string, storagePath: string, fileType: string, fileName: string) => {
    try {
      console.log(`Starting document processing for ${docId}: ${fileName}`)
      
      // Get conversation_id from the document record
      const { data: docRecord, error: docRecordError } = await supabase
        .from('opportunity_document_uploads')
        .select('conversation_id, status')
        .eq('id', docId)
        .single()

      if (docRecordError) {
        console.error('Error fetching document record:', docRecordError)
        throw docRecordError
      }

      const convId = docRecord?.conversation_id || currentConversationId
      console.log(`Document ${docId} conversation_id: ${convId}, current status: ${docRecord?.status}`)

      // Call the manual-index-processor edge function to extract and index the document
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('No session found')
      }

      console.log(`Calling manual-index-processor for document ${docId}`)
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/manual-index-processor`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            document_id: docId,
            storage_path: storagePath,
            file_type: fileType,
            file_name: fileName,
            document_type: 'opportunity_document_uploads',
          }),
        }
      )

      const responseText = await response.text()
      console.log(`Indexing response status: ${response.status}, body: ${responseText.substring(0, 500)}`)
      
      let responseData: any = null
      
      try {
        responseData = JSON.parse(responseText)
        console.log(`Parsed response data:`, JSON.stringify(responseData, null, 2))
      } catch (parseError) {
        console.warn('Response is not JSON:', responseText.substring(0, 200))
        console.error('JSON parse error:', parseError)
      }

      if (!response.ok) {
        console.error('Indexing request failed:', response.status, responseText)
        
        // Check if it's a "no documents to process" response (which is actually OK)
        if (responseData?.message?.includes('No documents to process')) {
          console.log('Document queued for processing, will poll for status')
        } else if (responseData?.error) {
          // Update document with error details but keep it visible
          await supabase
            .from('opportunity_document_uploads')
            .update({ 
              status: 'error',
              updated_at: new Date().toISOString()
            })
            .eq('id', docId)
          
          toast({
            title: 'Indexing Error',
            description: responseData.error || 'Failed to process document. Please try uploading again.',
            variant: 'destructive',
          })
          
          await fetchUploadedDocuments()
          return
        } else {
          // Unknown error - log full response
          console.error('Unknown error from manual-index-processor:', {
            status: response.status,
            statusText: response.statusText,
            body: responseText
          })
        }
      } else {
        // Check response for processing status
        console.log('Processing response data:', responseData)
        if (responseData?.processed && responseData.processed.length > 0) {
          const processed = responseData.processed.find((p: any) => p.id === docId)
          console.log(`Found processed document:`, processed)
          if (processed && processed.status === 'indexed') {
            console.log(`Document ${docId} indexed immediately`)
            await fetchUploadedDocuments()
            
            // Verify chunks exist
            const { data: chunks, error: chunksError } = await supabase
              .from('document_chunks')
              .select('id, chunk_text')
              .eq('source_document_id', docId)
              .eq('source_type', 'opportunity_document_uploads')
              .limit(5)
            
            if (chunksError) {
              console.error('Error checking chunks:', chunksError)
            } else {
              console.log(`Found ${chunks?.length || 0} chunks for document ${docId}`)
              if (chunks && chunks.length > 0) {
                console.log('Sample chunk:', chunks[0].chunk_text.substring(0, 100))
              }
            }
            
            // Analyze immediately
            if (convId) {
              await new Promise(resolve => setTimeout(resolve, 1000))
              analyzeDocument(docId, convId).catch(err => {
                console.error(`Error analyzing document ${docId}:`, err)
              })
            }
            return
          } else if (processed && processed.status === 'error') {
            console.error(`Document ${docId} processing failed:`, processed.error)
            toast({
              title: 'Processing Failed',
              description: processed.error || 'Document processing failed',
              variant: 'destructive',
            })
            await fetchUploadedDocuments()
            return
          }
        } else if (responseData?.errors && responseData.errors.length > 0) {
          const errorDoc = responseData.errors.find((e: any) => e.id === docId)
          if (errorDoc) {
            console.error(`Document ${docId} processing error:`, errorDoc.error)
            toast({
              title: 'Processing Error',
              description: errorDoc.error || 'Document processing error',
              variant: 'destructive',
            })
            await fetchUploadedDocuments()
            return
          }
        }
      }

      // Poll for indexing completion (up to 60 seconds)
      let attempts = 0
      const maxAttempts = 30
      let isIndexed = false

      console.log(`Starting polling for document ${docId} indexing status`)

      while (attempts < maxAttempts && !isIndexed) {
        attempts++
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        const { data: updatedDoc, error: checkError } = await supabase
          .from('opportunity_document_uploads')
          .select('status')
          .eq('id', docId)
          .single()

        if (checkError) {
          console.error(`Attempt ${attempts}: Error checking document status:`, checkError)
          // Continue polling - might be temporary
        } else if (updatedDoc) {
          console.log(`Attempt ${attempts}: Document status is ${updatedDoc.status}`)
          
          if (updatedDoc.status === 'indexed') {
            isIndexed = true
            console.log(`Document ${docId} successfully indexed after ${attempts} attempts`)
            
            // Verify chunks exist before analyzing
            const { data: chunks, error: chunksError } = await supabase
              .from('document_chunks')
              .select('id, chunk_text, source_type, source_document_id')
              .eq('source_document_id', docId)
              .eq('source_type', 'opportunity_document_uploads')
              .limit(5)
            
            if (chunksError) {
              console.error(`Error checking chunks for document ${docId}:`, chunksError)
            } else if (chunks && chunks.length > 0) {
              console.log(`Found ${chunks.length} chunks for document ${docId}`)
              console.log('Sample chunk data:', {
                id: chunks[0].id,
                source_type: chunks[0].source_type,
                source_document_id: chunks[0].source_document_id,
                text_preview: chunks[0].chunk_text?.substring(0, 100)
              })
              // Analyze the document to extract requirements and create todos
              if (convId) {
                await new Promise(resolve => setTimeout(resolve, 1000))
                analyzeDocument(docId, convId).catch(err => {
                  console.error(`Error analyzing document ${docId}:`, err)
                })
              }
            } else {
              console.warn(`Document ${docId} marked as indexed but no chunks found. Checking all chunks for this document...`)
              // Debug: Check if chunks exist with different query
              const { data: allChunks } = await supabase
                .from('document_chunks')
                .select('id, source_type, source_document_id')
                .eq('source_document_id', docId)
                .limit(10)
              console.log(`Found ${allChunks?.length || 0} total chunks for document ${docId} (any source_type)`)
              if (allChunks && allChunks.length > 0) {
                console.log('Chunk source types:', allChunks.map(c => c.source_type))
              }
            }
          } else if (updatedDoc.status === 'error') {
            console.error(`Document ${docId} failed to index after ${attempts} attempts`)
            toast({
              title: 'Document Processing Failed',
              description: 'The document could not be processed. Please check the file format and try again.',
              variant: 'destructive',
            })
            break
          }
        }
      }

      if (!isIndexed) {
        console.warn(`Document ${docId} not yet indexed after ${attempts} attempts (${attempts * 2}s). Will continue checking in background.`)
      }

      await fetchUploadedDocuments()
    } catch (error: any) {
      console.error('Error processing document:', error)
      
      // Update status to error but keep document visible
      await supabase
        .from('opportunity_document_uploads')
        .update({ 
          status: 'error',
          updated_at: new Date().toISOString()
        })
        .eq('id', docId)
      
      await fetchUploadedDocuments()
      
      toast({
        title: 'Processing Error',
        description: error.message || 'Failed to process document. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    let conversationId = currentConversationId

    // Create conversation if needed
    if (!conversationId) {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
          toast({
            title: 'Error',
            description: 'Please log in to continue',
            variant: 'destructive',
          })
          return
        }

        const { data, error } = await supabase
          .from('proposal_conversations')
            .insert({
                user_id: user.id,
            title: 'New Proposal',
            bid_type: bidType,
            output_format: outputFormat,
          })
          .select()
          .single()

        if (error) throw error
        if (!data) {
          throw new Error('Failed to create conversation')
        }

        conversationId = data.id
        setCurrentConversationId(data.id)
        await fetchConversations()
        // Don't fetch messages here - we'll add them locally and they'll be saved to DB
        // This prevents the fetchMessages from clearing the messages before we add the user message
    } catch (error: any) {
        console.error('Error creating conversation:', error)
        toast({
          title: 'Error',
          description: error.message || 'Failed to create conversation',
          variant: 'destructive',
        })
        return
      }
    }

    const userMessage = input.trim()
    setInput('')
    setIsLoading(true)
    isSendingMessageRef.current = true // Set flag to prevent fetchMessages from overwriting

    // Check if there are documents still processing
    const processingDocs = uploadedDocuments.filter(doc => 
      doc.status === 'uploaded' || doc.status === 'processing'
    )
    const indexedDocs = uploadedDocuments.filter(doc => doc.status === 'indexed')
    
    // If user is asking about documents but they're still processing
    if (processingDocs.length > 0 && /analyze|review|examine|look|read|document/i.test(userMessage.toLowerCase())) {
      toast({
        title: 'Documents Still Processing',
        description: `Please wait for ${processingDocs.length} document(s) to finish indexing. Status will update automatically.`,
        variant: 'default',
      })
      setIsLoading(false)
      return
    }
    
    // If user is asking to analyze but no documents are indexed
    if (indexedDocs.length === 0 && /analyze|review|examine|look through|read|what does|what is in/i.test(userMessage.toLowerCase())) {
      toast({
        title: 'No Documents Available',
        description: 'Please upload and wait for documents to be indexed before analyzing them.',
        variant: 'default',
      })
      setIsLoading(false)
      return
    }

    // Add user message to UI
    const userMessageId = `user-${Date.now()}`
    const newUserMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
      conversation_id: conversationId || undefined, // Track which conversation this belongs to
      metadata: {
        template: 'standard',
        section: 'full',
        tone,
        length,
        use_rag: useRAG,
        attached_documents: attachedDocumentIds,
      },
    }

    setMessages(prev => [...prev, newUserMessage])

    // Save user message to database
    try {
      const { error } = await supabase
        .from('proposal_messages')
                .insert({
          conversation_id: conversationId!,
          role: 'user',
          content: userMessage,
          metadata: newUserMessage.metadata,
        })

      if (error) throw error
    } catch (error) {
      console.error('Error saving user message:', error)
    }

    // Add placeholder for assistant response
    const assistantMessageId = `assistant-${Date.now()}`
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: 'Thinking...',
      created_at: new Date().toISOString(),
      conversation_id: conversationId || undefined, // Track which conversation this belongs to
    }])

    try {
      // Call RAG query endpoint
      const { data: { session } } = await supabase.auth.getSession()
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User not authenticated')
      }
      
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/rag-query`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
            ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            query: userMessage,
            userId: user.id, // Required by the edge function
            // Only include template/section if user explicitly asks for proposal generation
            template: /generate|create|draft|write.*proposal|proposal.*section/i.test(userMessage) ? 'standard' : undefined,
            section: /generate|create|draft|write.*proposal|proposal.*section/i.test(userMessage) ? 'full' : undefined,
            tone,
            length,
            opportunityId: null, // Can be enhanced later
            conversationId: conversationId, // Pass conversation ID for memory
            bidType,
            outputFormat,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorCode = errorData?.code;
        
        // Handle specific token limit error codes
        if (response.status === 403) {
          let errorMessage = '';
          switch (errorCode) {
            case 'NO_SUBSCRIPTION':
              errorMessage = 'You need an active subscription to use AI Proposal Generator. Please upgrade your plan.';
              break;
            case 'TRIAL_LIMIT':
              errorMessage = 'You\'ve reached your trial AI request limit (50 requests). Please upgrade to a paid plan to continue.';
              break;
            case 'TOKEN_LIMIT':
              errorMessage = 'You\'ve used all your AI tokens for this billing period. Purchase additional tokens in Settings > Billing.';
              break;
            case 'FREE_TIER':
              errorMessage = 'AI Proposal Generator is available on Professional and Premium plans. Upgrade your subscription to access this feature.';
              break;
            default:
              errorMessage = errorData?.error || 'Access denied';
          }
          throw new Error(errorMessage)
        }
        
        throw new Error(errorData.error || 'Failed to get response from AI')
      }

      const data = await response.json()
      let assistantContent = data.response || data.text || 'No response generated'
      
      // If no context was used but documents exist, provide helpful message
      if (!data.context_used && indexedDocs.length > 0 && /analyze|review|examine|look|read|document/i.test(userMessage.toLowerCase())) {
        assistantContent = `I notice you're asking about documents, but I wasn't able to retrieve their content. This could mean:
        
1. The documents are still being processed (check the status below)
2. The documents may need to be re-indexed
3. There may be an issue with document retrieval

Please try again in a moment, or re-upload the documents if the issue persists.

${assistantContent}` // Still include the original response
      }

      // Update assistant message
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId 
          ? { ...msg, content: assistantContent }
          : msg
      ))

      // Note: Document export is now only triggered when user explicitly requests it via UI
      // Removed automatic export detection to prevent unwanted downloads

      // Save assistant message to database
      await supabase
        .from('proposal_messages')
        .insert({
          conversation_id: conversationId!,
          role: 'assistant',
          content: assistantContent,
          metadata: {
            template: 'standard',
            section: 'full',
            tone,
            length,
            use_rag: useRAG,
          },
        })

      // Update conversation title if it's the first message
      if (messages.length === 0) {
        const title = userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : '')
        await supabase
          .from('proposal_conversations')
          .update({ 
            title,
            updated_at: new Date().toISOString()
          })
          .eq('id', conversationId!)
        
        await fetchConversations()
      }

      // Refresh uploaded documents to get latest status
      await fetchUploadedDocuments()

    } catch (error: any) {
      console.error('Error getting AI response:', error)
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId 
          ? { ...msg, content: `Error: ${error.message || 'Failed to get response. Please try again.'}` }
          : msg
      ))
      } finally {
      setIsLoading(false)
      isSendingMessageRef.current = false // Clear flag after message is sent
      // Refresh messages from DB to get the saved messages with proper IDs
      if (conversationId) {
        setTimeout(() => fetchMessages(conversationId), 500)
      }
    }
  }

  const handleDeleteClick = (conversationId: string) => {
    setConversationToDelete(conversationId)
    setIsDeleteDialogOpen(true)
  }

  const deleteConversation = async () => {
    if (!conversationToDelete) return

    try {
      const { error } = await supabase
        .from('proposal_conversations')
        .delete()
        .eq('id', conversationToDelete)

      if (error) throw error

      if (currentConversationId === conversationToDelete) {
        setCurrentConversationId(null)
        setMessages([])
      }

      await fetchConversations()
      toast({
        title: 'Success',
        description: 'Conversation deleted',
      })
    } catch (error) {
      console.error('Error deleting conversation:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete conversation',
        variant: 'destructive',
      })
    } finally {
      setIsDeleteDialogOpen(false)
      setConversationToDelete(null)
    }
  }

  const exportDocument = async (content: string, format: 'pdf' | 'docx' | 'xlsx' | 'txt', messageId: string) => {
    try {
      setIsLoading(true)
      
      const response = await fetch('/api/export-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          format,
          filename: `proposal-${messageId.slice(-8)}`,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to generate document')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      
      // Create download link
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = `proposal-${messageId.slice(-8)}.${format}`
      
      // Append to body to ensure it works in all browsers
      document.body.appendChild(a)
      
      // Trigger click
      a.click()
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 100)
      
      toast({
        title: 'Document Created',
        description: `Your ${format.toUpperCase()} file has been generated and downloaded.`,
      })
    } catch (error: any) {
      console.error('Error exporting document:', error)
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to generate document',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

    // (SidebarContent removed — using Sheet drawer instead)

    // Show loading state while checking subscription
    if (isSubscriptionLoading) {
      return (
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )
    }

    // Restrict access to professional tier and above
    if (!isProfessional) {
      return (
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">AI Proposal Generator</CardTitle>
              <p className="text-muted-foreground mt-2">
                Create winning proposals in minutes with our AI-powered proposal generator.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="font-medium text-sm">This feature includes:</p>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span> Upload & analyze RFP/RFQ documents
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span> Generate proposal sections automatically
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span> Reference your company knowledge base
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span> Export to PDF, Word, and more
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Professional Plan Required</strong>
                  <br />
                  Upgrade to Professional to unlock the AI Proposal Generator and other advanced AI features.
                </p>
              </div>
              <Button asChild className="w-full" size="lg">
                <a href="/pricing">Upgrade to Professional</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      )
    }

    return (
    <div className="container mx-auto px-4 py-4 max-w-5xl flex flex-col dashboard-cards" style={{ height: 'calc(100vh - 120px)' }}>
      {/* History Drawer */}
      <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <SheetContent side="left" className="w-80 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-border/50">
            <SheetTitle className="text-left flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Proposal History
            </SheetTitle>
          </SheetHeader>
          <div className="p-3 border-b border-border/50 flex-shrink-0">
            <Button
              onClick={() => {
                createNewConversation()
                setIsHistoryOpen(false)
              }}
              className="w-full h-9 text-sm font-medium"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Proposal
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="px-3 py-2 space-y-1">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <p>No conversations yet.</p>
                  <p className="mt-1">Start a new proposal to create one.</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={cn(
                      "px-3 py-2.5 rounded-lg cursor-pointer transition-all relative group",
                      currentConversationId === conv.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "hover:bg-muted/50"
                    )}
                    onClick={() => {
                      setCurrentConversationId(conv.id)
                      setIsHistoryOpen(false)
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className={cn(
                        "h-3.5 w-3.5 shrink-0",
                        currentConversationId === conv.id ? "text-primary-foreground/70" : "text-muted-foreground"
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" title={conv.title || 'Untitled Proposal'}>
                          {conv.title || 'Untitled Proposal'}
                        </p>
                        <p className={cn(
                          "text-[11px] mt-0.5",
                          currentConversationId === conv.id ? "text-primary-foreground/60" : "text-muted-foreground"
                        )}>
                          {new Date(conv.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
                          "hover:bg-destructive/10 hover:text-destructive",
                          currentConversationId === conv.id && "text-primary-foreground/70"
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick(conv.id)
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <Card className="flex flex-col overflow-hidden flex-1">
        {/* Chat Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20">
              <PenLine className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">
                Proposal AI
              </p>
              <p className="text-[11px] text-muted-foreground">
                {currentConversation ? truncateWords(currentConversation.title || 'New Proposal', 6) : 'Create winning proposals with AI'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* History button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsHistoryOpen(true)}
              className="text-xs gap-1 text-muted-foreground h-7"
            >
              <MessageSquare className="h-3 w-3" />
              History
              {conversations.length > 0 && (
                <span className="ml-0.5 text-[10px] bg-muted rounded-full px-1.5 py-0.5 font-medium">
                  {conversations.length}
                </span>
              )}
            </Button>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={createNewConversation} className="text-xs gap-1 text-muted-foreground h-7">
                <Plus className="h-3 w-3" /> New
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="max-w-4xl mx-auto flex flex-col h-full space-y-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center flex-1 min-h-[60vh]">
                <div className="text-center max-w-lg">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                    <PenLine className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Proposal AI</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                    Upload opportunity documents or ask me to generate proposal sections. I reference your company knowledge base and uploaded docs.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md mx-auto">
                    {[
                      'Analyze uploaded docs for requirements',
                      'Generate an executive summary',
                      'Create a technical approach section',
                      'Draft a full proposal outline',
                    ].map((prompt, idx) => (
                      <button
                        key={idx}
                        className="flex items-start gap-2 text-left text-xs p-3 rounded-xl border hover:bg-primary/5 hover:border-primary/30 transition-all duration-200 group"
                        onClick={() => { setInput(prompt); }}
                      >
                        <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                        <span className="leading-snug">{prompt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <PenLine className="h-3.5 w-3.5 text-primary" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-2xl px-4 py-3 max-w-[85%] relative",
                        message.role === 'user'
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-muted/70 rounded-tl-sm"
                      )}
                    >
                      {message.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none text-sm [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:mb-2 [&>ol]:mb-2 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm">
                            <ReactMarkdown
                              components={{
                                a: ({ node, href, children, ...props }) => {
                                  if (href && (href.startsWith('command:export-') || href.startsWith('#export-'))) {
                                    const format = (href.startsWith('command:export-') 
                                      ? href.split('export-')[1] 
                                      : href.split('#export-')[1]) as 'pdf' | 'docx' | 'xlsx' | 'txt'
                                      
                                    return (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-7 gap-1.5 not-prose inline-flex my-1 bg-primary/10 hover:bg-primary/20 border-primary/20 text-xs rounded-lg"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          e.nativeEvent.stopImmediatePropagation()
                                          const contentToExport = message.content
                                            .replace(/\[.*?\]\(command:export-.*?\)/g, '')
                                            .replace(/\[.*?\]\(#export-.*?\)/g, '')
                                            .trim()
                                          exportDocument(contentToExport, format, message.id)
                                          return false
                                        }}
                                      >
                                        <Download className="h-3.5 w-3.5" />
                                        {children}
                                      </Button>
                                    )
                                  }
                                  return <a href={href} {...props} target="_blank" rel="noopener noreferrer">{children}</a>
                                }
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        {/* File Upload Area */}
        {selectedFiles.length > 0 && (
          <div className="border-t px-5 py-3 bg-muted/20 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap max-w-4xl mx-auto">
              {selectedFiles.map((file, idx) => (
                <Badge key={idx} variant="secondary" className="gap-1.5 rounded-full px-3 py-1">
                  <FileIcon className="h-3 w-3" />
                  <span className="text-xs">{file.name}</span>
                  <Button 
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-0.5 hover:bg-destructive/10 hover:text-destructive rounded-full"
                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              <Button 
                size="sm"
                onClick={handleUploadFiles}
                disabled={isLoading}
                className="rounded-full h-7 text-xs gap-1.5"
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload {selectedFiles.length} file(s)
              </Button>
            </div>
          </div>
        )}

        {/* Uploaded Documents Status */}
        {uploadedDocuments.length > 0 && (
          <div className="border-t px-5 py-3 bg-muted/10 flex-shrink-0">
            <div className="flex flex-col gap-2 max-w-4xl mx-auto">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Documents</span>
              <div className="flex items-center gap-2 flex-wrap">
                {uploadedDocuments.map((doc) => (
                  <Badge 
                    key={doc.id} 
                    variant={
                      doc.status === 'indexed' 
                        ? 'default' 
                        : doc.status === 'processing' 
                        ? 'secondary' 
                        : doc.status === 'uploaded'
                        ? 'outline'
                        : 'destructive'
                    }
                    className={cn(
                      "gap-1.5 px-2.5 py-1 pr-1 relative group",
                      doc.status === 'uploaded' && "border-blue-300 text-blue-700 dark:text-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-950/20",
                      doc.status === 'processing' && "border-amber-300 text-amber-700 dark:text-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/20",
                      doc.status === 'indexed' && "bg-green-50 dark:bg-green-950/20 border-green-300 text-green-700 dark:text-green-400 dark:border-green-600",
                      doc.status === 'error' && "bg-red-50 dark:bg-red-950/20 border-red-300 text-red-700 dark:text-red-400 dark:border-red-600"
                    )}
                  >
                    <FileText className="h-3 w-3" />
                    <span className="max-w-[200px] truncate" title={doc.file_name}>{doc.file_name}</span>
                    {doc.status === 'processing' && (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin ml-1" />
                        <span className="text-xs ml-1">Processing...</span>
                      </>
                    )}
                    {doc.status === 'uploaded' && (
                      <span className="text-xs ml-1">Queued...</span>
                    )}
                    {doc.status === 'indexed' && (
                      <span className="text-xs ml-1 text-green-600 dark:text-green-400">✓ Ready</span>
                    )}
                    {doc.status === 'error' && (
                      <span className="text-xs ml-1 text-red-600 dark:text-red-400">✗ Error</span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 ml-1 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-opacity"
                      onClick={async (e) => {
                        e.stopPropagation()
                        await removeDocument(doc.id)
                      }}
                      title="Remove document from conversation"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t px-5 py-4 bg-background/50 flex-shrink-0 mt-auto space-y-3">
          {/* Settings Row */}
          <div className="flex items-center gap-2">
            {/* Model selector pill */}
            {!isMobile && (
              <DropdownMenu open={isModelDropdownOpen} onOpenChange={setIsModelDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/50 border text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span>
                      {selectedModel === 'gemini-2.5' && 'Gemini'}
                      {selectedModel === 'grok' && 'Grok'}
                      {selectedModel === 'openai' && 'OpenAI'}
                      {selectedModel === 'anthropic' && 'Anthropic'}
                    </span>
                    <ChevronLeft className="h-3 w-3 rotate-[-90deg]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuLabel>AI Models</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setSelectedModel('gemini-2.5'); setIsModelDropdownOpen(false) }} className="cursor-pointer">
                    <div className="flex items-center justify-between w-full">
                      <span>Gemini</span>
                      {selectedModel === 'gemini-2.5' && <span className="text-xs text-primary">✓</span>}
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="cursor-pointer opacity-50">
                    <div className="flex items-center justify-between w-full">
                      <span>Grok</span>
                      <span className="text-xs text-muted-foreground">Coming Soon</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="cursor-pointer opacity-50">
                    <div className="flex items-center justify-between w-full">
                      <span>OpenAI</span>
                      <span className="text-xs text-muted-foreground">Coming Soon</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="cursor-pointer opacity-50">
                    <div className="flex items-center justify-between w-full">
                      <span>Anthropic</span>
                      <span className="text-xs text-muted-foreground">Coming Soon</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Sheet open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
              <SheetTrigger asChild>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 border transition-all">
                  <Settings className="h-3 w-3" />
                  Settings
                </button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>AI Settings</SheetTitle>
                  <SheetDescription>
                    Customize your AI proposal generation settings
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div>
                    <Label className="mb-2">Bid Type</Label>
                    <Select value={bidType} onValueChange={setBidType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RFP">RFP</SelectItem>
                        <SelectItem value="RFQ">RFQ</SelectItem>
                        <SelectItem value="IFB">IFB</SelectItem>
                        <SelectItem value="Sources Sought">Sources Sought</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2">Output Format</Label>
                    <Select value={outputFormat} onValueChange={setOutputFormat}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="markdown">Markdown</SelectItem>
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="word">Word</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2">Tone</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="persuasive">Persuasive</SelectItem>
                        <SelectItem value="concise">Concise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2">Length</Label>
                    <Select value={length} onValueChange={setLength}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="long">Long</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Input Row */}
          <form onSubmit={handleSendMessage} className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              accept=".pdf,.doc,.docx,.txt"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl hover:bg-muted"
              onClick={() => fileInputRef.current?.click()}
              title="Upload documents"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about proposals, upload docs, or start drafting..."
              className="flex-1 h-10 text-sm rounded-xl pr-4 pl-4 border-muted-foreground/20 focus-visible:ring-primary/30"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage(e)
                }
              }}
            />
            <Button 
              type="submit" 
              disabled={isLoading || !input.trim()} 
              size="icon"
              className="h-10 w-10 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 transition-all shrink-0"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </Card>

      {/* Load PDF.js from CDN - use unpkg which has better UMD support */}
      <Script
        src="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js"
        strategy="lazyOnload"
        onLoad={() => {
          // PDF.js UMD build exposes pdfjsLib on window
          const pdfjs = (window as any).pdfjsLib || (window as any).pdfjsDist || (window as any).pdfjs
          if (pdfjs && pdfjs.GlobalWorkerOptions) {
            // Set worker source
            pdfjs.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the conversation
              and all its messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConversationToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConversation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
} 
